// Not ported (dead in the Angular app): none. selectClient/selectedClient/notify*/errorMessage live elsewhere.
import { api } from '@/core/http/apiClient';
import { exportFile } from '@/core/http/helpers';
import type { ReportLogoInfo } from '@/core/models/firm-settings.models';
import type {
  AuthorisationCodeResponse,
  ClientDashboard,
  ClientFinancialOverview,
  ClientDetail,
  ClientGridQuery,
  ClientGridResponse,
  ClientPeriods,
  CompanyLookupResult,
  CompanySearchResponse,
  ContactOption,
  ExportQuery,
  PeriodRow,
  PeriodSuggestion,
  SaveClientPayload,
  SavePeriodPayload,
  SaveUserPayload,
  SideListResponse,
  UserDetail,
  UserGridQuery,
  UserGridResponse,
} from '@/core/models/practice.models';

/** Typed client for the clients, users, profile and accounting-period endpoints. */
export const practiceService = {
  // ---- Clients ----

  getClientGrid: (query: ClientGridQuery) => api.post<ClientGridResponse>('Client/getClientGridData', query),

  getNextClientId: () => api.get<{ clientId: string; autoClientId: number }>('Client/getAutoClientId'),

  getClient: (id: string) => api.get<ClientDetail>(`Client/getClientDetails/${id}`),

  saveClient: (payload: SaveClientPayload) =>
    api.post<{ universalId: string; clientMessage: string }>('Client/saveClient', payload),

  // QA rows 218/221: a client's own report logo (Edit Client > Upload Logo).
  getClientLogo: (clientId: string) => api.get<ReportLogoInfo>(`Client/getClientLogo/${clientId}`),

  getClientLogoImage: (clientId: string) =>
    api.get<Blob>(`Client/getClientLogoImage/${clientId}`, { responseType: 'blob' }),

  uploadClientLogo: (clientId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return api.post<ReportLogoInfo>(`Client/uploadClientLogo/${clientId}`, formData);
  },

  deleteClientLogo: (clientId: string) => api.post<ReportLogoInfo>(`Client/deleteClientLogo/${clientId}`, {}),

  deleteClients: (ids: string[]) => api.post<boolean>('Client/deleteClients', ids),

  restoreClients: (ids: string[]) => api.post<boolean>('Client/restoreClients', ids),

  setClientStatus: (clientIds: string[], isActive: boolean) =>
    api.post<boolean>('Client/setClientStatus', { clientIds, isActive }),

  lookupCompany: (companyNumber: string) =>
    api.get<CompanyLookupResult>(`Client/lookupCompany/${encodeURIComponent(companyNumber)}`),

  // Query string is built inline (not params) so encoding matches Angular.
  searchCompany: (name: string, limit = 20) =>
    api.get<CompanySearchResponse>(`Client/searchCompany?name=${encodeURIComponent(name)}&limit=${limit}`),

  getClientSideList: (pageNumber: number, pageSize: number, search: string) =>
    api.post<SideListResponse>('Client/getSideClientList', { pageNumber, pageSize, search }),

  getAuthorisationCodes: (search: string, filter: number) =>
    api.post<AuthorisationCodeResponse>('Client/getAuthorisationCodes', {
      pageNumber: 1,
      pageSize: 1000,
      search,
      filter,
    }),

  saveAuthorisationCode: (clientId: string, authCode: string | null) =>
    api.post<boolean>('Client/saveAuthorisationCode', { clientId, authCode }),

  exportClients: (query: ExportQuery) => exportFile('Client/exportClients', query, !!query.isPrint),

  exportAuthorisationCodes: (query: ExportQuery) =>
    exportFile('Client/exportAuthorisationCodes', query, !!query.isPrint),

  // ---- Users ----

  getUserGrid: (query: UserGridQuery) => api.post<UserGridResponse>('User/getUserList', query),

  getUser: (id: string) => api.get<UserDetail>(`User/getUser/${id}`),

  saveUser: (payload: SaveUserPayload) =>
    api.post<{ universalId: string; message: string }>('User/saveUserClients', payload),

  deleteUsers: (ids: string[]) => api.post<boolean>('User/deleteUser', ids),

  restoreUsers: (ids: string[]) => api.post<boolean>('User/restoreUser', ids),

  getContactOptions: () => api.get<ContactOption[]>('User/getContactOptions'),

  exportUsers: (query: ExportQuery) => exportFile('User/exportUsers', query, !!query.isPrint),

  // ---- Accounting periods ----

  getClientPeriods: (clientId: string) => api.get<ClientPeriods>(`AccountingPeriod/getClientPeriods/${clientId}`),

  getPriorPeriod: (clientId: string) => api.get<PeriodSuggestion>(`AccountingPeriod/getPriorPeriod/${clientId}`),

  getNextPeriod: (clientId: string) => api.get<PeriodSuggestion>(`AccountingPeriod/getNextPeriod/${clientId}`),

  savePeriod: (payload: SavePeriodPayload) => api.post<PeriodRow>('AccountingPeriod/savePeriod', payload),

  deletePeriod: (id: string) => api.post<boolean>(`AccountingPeriod/deletePeriod/${id}`, {}),

  lockPeriod: (id: string, value: boolean) => api.post<PeriodRow>(`AccountingPeriod/lockPeriod/${id}`, { value }),

  ceasePeriod: (id: string, value: boolean) => api.post<PeriodRow>(`AccountingPeriod/ceasePeriod/${id}`, { value }),

  markComparative: (id: string, value: boolean) =>
    api.post<PeriodRow>(`AccountingPeriod/markComparative/${id}`, { value }),

  setAutoPeriodGeneration: (clientId: string, value: boolean) =>
    api.post<boolean>(`AccountingPeriod/setAutoPeriodGeneration/${clientId}`, { value }),

  // ---- Client dashboard ----

  getClientDashboard: (clientId: string) => api.get<ClientDashboard>(`ClientDashboard/getDashboard/${clientId}`),

  /** APT378: Turnover, Total income, Cost of sales and Expenses of a period (the latest one when no period is given). */
  getFinancialOverview: (clientId: string, periodId?: string) =>
    api.get<ClientFinancialOverview>(
      `ClientDashboard/getFinancialOverview/${clientId}`,
      periodId ? { params: { periodId } } : undefined,
    ),
};
